import { faDownload } from "@fortawesome/pro-light-svg-icons";
import {
    faExpand,
    faInfoCircle,
    faMinimize,
} from "@fortawesome/sharp-light-svg-icons";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useRef, useState } from "react";
import { useAnalyticsApiClient } from "../../../hooks";
import { ExpandableArea, useAreaExpansion } from "../../../ui/ExpandableArea";
import { FloatingMenu } from "../../../ui/Menu";
import { Modal } from "../../../ui/Modals";
import type { AnalyticsFilters } from "../state";
import { getQuarterDateRange } from "../utils/quarterUtils";
import { useFilterText } from "../utils/useFilterText";
import { useImageDownload } from "../utils/useImageDownload";
import { AvgPlot } from "./avgPlot";

interface AvgPerSiteProps {
    filters: AnalyticsFilters;
}

export const AvgPerSite = ({ filters }: AvgPerSiteProps) => {
    const analyticsApi = useAnalyticsApiClient();
    const [showInfoModal, setShowInfoModal] = useState(false);
    const svgRef = useRef<SVGSVGElement>(null);
    const filterText = useFilterText();
    const { downloadImage } = useImageDownload(
        svgRef,
        "avg_per_site",
        filterText,
    );

    const siteQuery = useQuery({
        queryKey: [
            "analtyicsSiteAverages",
            filters.quarterFrom,
            filters.quarterTo,
            filters.detectionLimit,
            filters.providers,
            filters.locationPreset,
            filters.siteType,
        ],
        queryFn: async () => {
            const startRange = getQuarterDateRange(filters.quarterFrom);
            const endRange = getQuarterDateRange(filters.quarterTo);

            return await analyticsApi.analyticsEmissionsDailyAveragesList({
                pageSize: 25,
                timestampAfter: startRange.start,
                timestampBefore: endRange.end,
                detectionThresholdGte: (filters.detectionLimit ?? 100) * 1000,
                providerWithSource:
                    filters.providers === "all" || !filters.providers
                        ? undefined
                        : (JSON.stringify(filters.providers) as any),
                locationPreset: filters.locationPreset?.length
                    ? filters.locationPreset
                    : undefined,
                siteType: filters.siteType?.length
                    ? filters.siteType
                    : undefined,
            });
        },
        refetchOnWindowFocus: false,
        staleTime: 300,
    });

    const { expandedCardId, expandCard, contractCard } = useAreaExpansion();
    const extraActions = useMemo(() => {
        const actions = [
            {
                icon: faInfoCircle,
                title: "Plot information",
                fn: () => setShowInfoModal(true),
            },
            {
                icon: faDownload,
                title: "Download plot as image (JPEG)",
                fn: downloadImage as () => void,
            },
        ];

        const isExpanded = expandedCardId === "avg_per_site";
        return [
            ...actions,
            {
                icon: isExpanded ? faMinimize : faExpand,
                title: isExpanded ? "Contract plot" : "Expand plot",
                fn: () =>
                    isExpanded ? contractCard() : expandCard("avg_per_site"),
            },
        ];
    }, [downloadImage, expandedCardId, contractCard, expandCard]);

    return (
        <>
            <ExpandableArea
                cardId="avg_per_site"
                expandable={true}
                className="h-full relative"
                content={
                    <div className="h-full p-1 group">
                        <FloatingMenu
                            actions={extraActions}
                            className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-400"
                        />
                        <AvgPlot
                            svgRef={svgRef}
                            siteData={siteQuery.data?.results || []}
                            loading={siteQuery.isLoading}
                            filters={filters}
                        />
                    </div>
                }
            />

            <Modal
                visible={showInfoModal}
                onClose={() => setShowInfoModal(false)}
                size="sm"
            >
                <div className="my-4 text-sm">
                    <h2 className="text-lg font-semibold mb-4">
                        Plot information
                    </h2>
                    <p className="mb-2">
                        This figure shows the average measured emission rate per
                        site for your company sites tracked by AERSHED. The
                        sites with the highest average are on the left side.
                        Only the 25 sites with the highest average are
                        displayed.
                    </p>
                    <p className="mb-2">
                        For example, with a {filters.detectionLimit} kg per hour
                        threshold, each bar is the mean of every detection at or
                        above {filters.detectionLimit} kg/h recorded at that
                        site over the selected period. Detections below the
                        threshold and non-detects are not included in the
                        average, so this is the typical size of the emissions we
                        did detect, not an average over all observations.
                    </p>
                    <p className="mb-2">
                        Because only detections are averaged, a site with a
                        single large detection can rank above a site that emits
                        frequently at a more moderate rate. Hover a bar to see
                        the site name and its exact average.
                    </p>
                    <p>
                        Data from providers that report at the equipment level
                        is aggregated to site level before averaging.
                    </p>
                </div>
            </Modal>
        </>
    );
};
